import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { roleAPI } from '../../../services/rbacApi';
import MultiSelect from '../../Common/MultiSelect';

/** Must be primary role only — excluded from additional roles picker (matches auth-service policy). */
export const PRIMARY_ONLY_ADDITIONAL_ROLE_CODES = ['SUPER_ADMIN'];

const isPrimaryOnlyAdditionalRole = (code) =>
  PRIMARY_ONLY_ADDITIONAL_ROLE_CODES.includes(String(code || '').toUpperCase());

const AdditionalRolesSelect = ({
  primaryRole,
  value = [],
  onChange,
  error,
}) => {
  const [allRoles, setAllRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingRoles(true);
    roleAPI.getAssignableRoles()
      .then((response) => {
        if (!cancelled) setAllRoles(response.data?.data?.roles || []);
      })
      .catch(() => {
        // non-fatal — dropdown stays empty
      })
      .finally(() => {
        if (!cancelled) setLoadingRoles(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleChange = useCallback(
    (next) => {
      const sanitized = (next || []).filter((code) => !isPrimaryOnlyAdditionalRole(code));
      onChange(sanitized);
    },
    [onChange]
  );

  // Drop primary-only codes if they were loaded from an older assignment or API
  useEffect(() => {
    const sanitized = (value || []).filter((code) => !isPrimaryOnlyAdditionalRole(code));
    if (sanitized.length !== value.length) {
      onChange(sanitized);
    }
  }, [value, onChange]);

  const options = useMemo(() => {
    const primary = (primaryRole || '').toUpperCase();
    return allRoles
      .filter(
        (r) =>
          r.code &&
          r.code.toUpperCase() !== primary &&
          !isPrimaryOnlyAdditionalRole(r.code)
      )
      .map((r) => ({
        value: r.code,
        label: `${r.name}${r.level != null ? ` (Level ${r.level})` : ''}`,
      }));
  }, [allRoles, primaryRole]);

  return (
    <div>
      <MultiSelect
        label="Additional roles"
        value={value.filter((code) => !isPrimaryOnlyAdditionalRole(code))}
        onChange={handleChange}
        options={options}
        placeholder={loadingRoles ? 'Loading roles...' : 'Select additional roles (optional)'}
        disabled={loadingRoles}
        error={error}
        menuMaxHeightClass="max-h-[min(70vh,32rem)]"
      />
      <p className="mt-1 text-xs text-gray-500">
        Optional. Permissions from all roles are combined at login. Super Admin must be set as the
        primary role only.
      </p>
    </div>
  );
};

export default AdditionalRolesSelect;
